// src/components/listing/BoostStatus.jsx
import React, { useState, useEffect } from 'react';
import { 
  FiClock, FiEye, FiTrendingUp, 
  FiRewind, FiAlertCircle,
  FiBarChart2, FiChevronRight, FiZap
} from 'react-icons/fi';
import { getListingBoosts } from '../../../services/api';
import Button from '../../common/Button';
import Loading from '../../common/Loading';

const BoostStatus = ({ 
  listingId, 
  showManageButton = false, 
  onManagePress, 
  showPerformance = false,
  onViewPerformance,
  onBoost
}) => {
  const [boosts, setBoosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBoostStatus();
  }, [listingId]);

  const fetchBoostStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getListingBoosts(listingId);
      setBoosts(response.data || []);
    } catch (error) {
      console.error('Error fetching boost status:', error);
      setError('Failed to load boost status');
    } finally {
      setLoading(false);
    }
  };

  const hasActiveBoost = boosts.length > 0;
  const activeBoost = hasActiveBoost ? boosts[0] : null;

  const getTimeRemaining = (expiresAt) => {
    if (!expiresAt) return 'Unknown';
    
    const now = new Date();
    const expires = new Date(expiresAt);
    const diffTime = expires - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
    
    if (diffTime <= 0) return 'Expired';
    if (diffDays > 1) return `${diffDays} days left`;
    if (diffDays === 1) return '1 day left';
    if (diffHours > 1) return `${diffHours} hours left`;
    return 'Less than 1 hour';
  };

  const getBoostTypeInfo = (boostType) => {
    switch (boostType) {
      case 'premium':
        return { 
          color: 'text-yellow-600', 
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          dotColor: 'bg-yellow-500',
          label: 'Premium Boost',
          icon: <FiZap className="w-4 h-4 text-yellow-600" />
        };
      case 'standard':
        return { 
          color: 'text-orange-600', 
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200',
          dotColor: 'bg-orange-500',
          label: 'Standard Boost',
          icon: <FiTrendingUp className="w-4 h-4 text-orange-600" />
        };
      case 'turbo':
        return { 
          color: 'text-purple-600', 
          bgColor: 'bg-purple-50',
          borderColor: 'border-purple-200',
          dotColor: 'bg-purple-500',
          label: 'Turbo Boost',
          icon: <FiZap className="w-4 h-4 text-purple-600" />
        };
      default:
        return { 
          color: 'text-orange-600', 
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200',
          dotColor: 'bg-orange-500',
          label: 'Active Boost',
          icon: <FiTrendingUp className="w-4 h-4 text-orange-600" />
        };
    }
  };

  const boostInfo = activeBoost ? getBoostTypeInfo(activeBoost.type) : null;

  if (loading) {
    return (
      <div className="mb-4">
        <div className="flex items-center gap-3 bg-gray-50 px-4 py-3 rounded-lg">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-600"></div>
          <span className="text-gray-600 text-sm">Checking boost status...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-4">
        <div className="flex items-center justify-between bg-red-50 border border-red-200 px-4 py-3 rounded-lg">
          <div className="flex items-center gap-3">
            <FiAlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-700 text-sm">Failed to load boost status</span>
          </div>
          <button
            onClick={fetchBoostStatus}
            className="p-1 hover:bg-red-100 rounded transition"
          >
            <FiRewind className="w-4 h-4 text-red-600" />
          </button>
        </div>
      </div>
    );
  }

  if (!hasActiveBoost) {
    return (
      <div className="mb-6">
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
              <FiTrendingUp className="w-6 h-6 text-gray-400" />
            </div>
            
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Not Boosted</h3>
              <p className="text-gray-600 text-sm mb-4">
                Boost your listing to get more visibility and sales
              </p>
              
              {showPerformance && onViewPerformance && (
                <button
                  onClick={onViewPerformance}
                  className="flex items-center gap-2 text-orange-600 hover:text-orange-700 text-sm font-medium"
                >
                  <FiBarChart2 className="w-4 h-4" />
                  View Performance
                </button>
              )}
            </div>
            
            {showManageButton && (
              <div className="flex-shrink-0">
                <Button
                  onClick={onBoost || onManagePress}
                  variant="primary"
                  size="small"
                  icon={<FiTrendingUp className="w-4 h-4" />}
                  iconPosition="left"
                >
                  Boost
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6">
      <div className={`border rounded-xl p-5 ${boostInfo.bgColor} ${boostInfo.borderColor}`}>
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${boostInfo.bgColor} border ${boostInfo.borderColor}`}>
              {boostInfo.icon}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-2 h-2 rounded-full ${boostInfo.dotColor}`}></div>
                <span className={`text-sm font-semibold ${boostInfo.color}`}>
                  {boostInfo.label}
                </span>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <FiClock className="w-3 h-3" />
                  <span>{getTimeRemaining(activeBoost.expires_at)}</span>
                </div>
                {activeBoost.impressions && (
                  <div className="flex items-center gap-1">
                    <FiEye className="w-3 h-3" />
                    <span>{activeBoost.impressions.toLocaleString()} views</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {showManageButton && (
            <Button
              onClick={onManagePress}
              variant="outline"
              size="small"
            >
              Manage
            </Button>
          )}
        </div>

        {/* Performance Metrics */}
        {showPerformance && onViewPerformance && (
          <div 
            className="mt-4 p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition cursor-pointer"
            onClick={() => onViewPerformance(activeBoost)}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <FiTrendingUp className="w-4 h-4 text-green-600" />
                <span className="text-sm font-semibold text-green-700">Boost Performance</span>
              </div>
              <FiChevronRight className="w-4 h-4 text-gray-400" />
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-3">
              {activeBoost.clicks && (
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{activeBoost.clicks}</div>
                  <div className="text-xs text-gray-500">Clicks</div>
                </div>
              )}
              {activeBoost.engagement_rate && (
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{activeBoost.engagement_rate}%</div>
                  <div className="text-xs text-gray-500">Engagement</div>
                </div>
              )}
            </div>
            
            <div className="pt-3 border-t border-gray-100">
              <button className="text-sm font-medium text-orange-600 hover:text-orange-700 flex items-center gap-1">
                View Full Analytics
                <FiChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Additional Metrics */}
        {(activeBoost.views || activeBoost.clicks || activeBoost.conversions) && (
          <div className="mt-4 grid grid-cols-3 gap-3">
            {activeBoost.views !== undefined && (
              <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
                <div className="text-lg font-bold text-gray-900">
                  {activeBoost.views.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500">Views</div>
              </div>
            )}
            {activeBoost.clicks !== undefined && (
              <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
                <div className="text-lg font-bold text-gray-900">
                  {activeBoost.clicks.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500">Clicks</div>
              </div>
            )}
            {activeBoost.conversions !== undefined && (
              <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
                <div className="text-lg font-bold text-gray-900">
                  {activeBoost.conversions.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500">Conversions</div>
              </div>
            )}
          </div>
        )}

        {/* Boost Details */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-gray-500">Boost ID</div>
              <div className="font-medium text-gray-900">{activeBoost.boost_id || 'N/A'}</div>
            </div>
            <div>
              <div className="text-gray-500">Started</div>
              <div className="font-medium text-gray-900">
                {activeBoost.created_at 
                  ? new Date(activeBoost.created_at).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })
                  : 'N/A'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoostStatus;